/**
 * AddPackageModal.tsx
 *
 * Multi-package purchase flow — the package-side analogue of ChargePaymentModal.
 * Staff add one or more packages (each with optional discounts) via
 * PackagesSection; all packages settle in ONE combined payment / one gateway
 * charge. Payment & Billing (merchant, source, terminal, manual, gift cards)
 * and the two-phase Forward charge operate on the combined net total.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, CheckCircle2, Gift } from 'lucide-react';
import {
  chargeForwardPayment,
  markTerminalTransactionComplete,
  listDiscounts,
  type BillingMethod,
  type Merchant,
  type PaymentTerminal,
  type TerminalCheckoutResult,
} from '@/services/payments.service';
import { listPatientFacilities } from '@/services/patients.service';
import {
  enrollInPackagesBatch,
  type CreatePatientPackagesBatchItem,
} from '@/services/packages.service';
import { getErrorMessage } from '@/services/apiClient';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import {
  GiftCardSelector,
  type AppliedGiftCard,
} from '@/components/payments/GiftCardSelector';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import {
  PaymentSourceSelector,
  parseTerminalToken,
  parseManualToken,
} from '@/components/payments/PaymentSourceSelector';
import {
  TerminalCheckoutPanel,
  type TerminalFlowResult,
} from '@/components/payments/TerminalCheckoutPanel';
import {
  PackagesSection,
  computePackageLineTotal,
  type PackageLine,
} from '@/components/packages/PackagesSection';

function fmtCurrency(value: number | null | undefined): string {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

/** Sentinel thrown from the purchase mutation to reach the declined result. */
class DeclinedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeclinedError';
  }
}

/* ── Main Modal ────────────────────────────────────────────────────── */

interface AddPackageModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

type Step = 'form' | 'review' | 'processing' | 'result';

export function AddPackageModal({
  patientId,
  open,
  onClose,
  onSaved,
}: AddPackageModalProps) {
  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState<string | null>(null);
  const [saveOverlay, setSaveOverlay] = useState<'saving' | 'saved' | null>(null);

  const [merchantId, setMerchantId] = useState<number | null>(null);
  const [paymentToken, setPaymentToken] = useState('new');
  const [selectedMethod, setSelectedMethod] = useState<BillingMethod | null>(null);
  const [selectedTerminal, setSelectedTerminal] = useState<PaymentTerminal | null>(null);
  const [terminalResult, setTerminalResult] = useState<TerminalFlowResult | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);

  const [packageLines, setPackageLines] = useState<PackageLine[]>([]);
  const [planId, setPlanId] = useState('');
  const [quoteId, setQuoteId] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [notes, setNotes] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');

  const [giftCards, setGiftCards] = useState<AppliedGiftCard[]>([]);
  const [resultStatus, setResultStatus] = useState<'success' | 'declined' | null>(null);
  const [declineMessage, setDeclineMessage] = useState<string | null>(null);

  // Guards the auto-submit `useEffect` so a captured terminal result fires
  // `purchaseMutation` exactly once. Reset on modal open so a new session
  // can auto-fire again. Mirrors ChargePaymentModal (Fix 13).
  const autoSubmittedRef = useRef<unknown>(null);

  const { data: facilities = [] } = useQuery({
    queryKey: ['patient-facilities'],
    queryFn: listPatientFacilities,
    staleTime: 5 * 60_000,
  });

  const { data: discounts = [] } = useQuery({
    queryKey: ['payment-discounts'],
    queryFn: () => listDiscounts(),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setError(null);
    setSaveOverlay(null);
    setMerchantId(null);
    setPaymentToken('new');
    setSelectedMethod(null);
    setSelectedTerminal(null);
    setTerminalResult(null);
    setSaveWarning(null);
    setPackageLines([]);
    setPlanId('');
    setQuoteId('');
    setFacilityId('');
    setNotes('');
    setReferenceNumber('');
    setGiftCards([]);
    setResultStatus(null);
    setDeclineMessage(null);
    // Re-arm the terminal auto-submit guard for the new modal session.
    autoSubmittedRef.current = null;
  }, [open]);

  // Combined post-discount total across all packages (pre-gift-card). Mirrors
  // productsTotal in ChargePaymentModal.
  const packagesTotal = useMemo(
    () => packageLines.reduce((sum, line) => sum + computePackageLineTotal(line, discounts), 0),
    [packageLines, discounts],
  );

  const giftCardTotal = useMemo(
    () => giftCards.reduce((sum, card) => sum + card.amount, 0),
    [giftCards],
  );

  const netChargeAmount = useMemo(
    () => Math.max(0, packagesTotal - giftCardTotal),
    [packagesTotal, giftCardTotal],
  );

  // Terminal-mode flags (identical semantics to ChargePaymentModal /
  // the prior single-package flow).
  const isTerminalMode = parseTerminalToken(paymentToken) !== null;
  const isTerminalCheckout = terminalResult?.mode === 'checkout';
  const isManualMode = parseManualToken(paymentToken) !== null;

  const canSubmit =
    packageLines.length > 0
    && (netChargeAmount === 0 || isManualMode || (paymentToken !== 'new' && !isTerminalMode) || isTerminalCheckout);

  const handleMerchantChange = useCallback((id: number, nextMerchant: Merchant) => {
    setMerchantId(id);
    setError(null);
    if (nextMerchant.facilityId) {
      setFacilityId(String(nextMerchant.facilityId));
    }
  }, []);

  const handleSourceChange = useCallback(
    (token: string, method: BillingMethod | null, terminal: PaymentTerminal | null = null) => {
      setPaymentToken(token);
      setSelectedMethod(method);
      setSelectedTerminal(terminal);
      setTerminalResult(null);
      setSaveWarning(null);
    },
    [],
  );

  const handleNewSourceSaved = useCallback((method: BillingMethod) => {
    setPaymentToken(method.token);
    setSelectedMethod(method);
    setSelectedTerminal(null);
    setTerminalResult(null);
    setSaveWarning(null);
    setError(null);
  }, []);

  const handleTerminalResult = useCallback((result: TerminalFlowResult) => {
    setTerminalResult(result);
    setSaveWarning(result.data.save_warning ?? null);
  }, []);

  const handleTerminalReset = useCallback(() => {
    setTerminalResult(null);
    setSaveWarning(null);
  }, []);

  const purchaseMutation = useMutation({
    mutationFn: async () => {
      setSaveOverlay('saving');
      setDeclineMessage(null);
      if (packageLines.length === 0) {
        throw new Error('No packages selected.');
      }

      let resolvedToken = paymentToken !== 'new' && !isTerminalMode && !isManualMode ? paymentToken : undefined;
      let resolvedTitle = selectedMethod?.title ?? 'Forward';
      // Terminal checkout hands back BOTH ids: the real payment id (pmt_*) as
      // transaction_id and the payment_intent id (pi_*). Keep them separate.
      let resolvedTransactionId: string | undefined;
      let resolvedIntentId: string | undefined;
      let resolvedManualPayment = false;
      const manualMethodId = parseManualToken(paymentToken);

      if (isManualMode && manualMethodId) {
        resolvedManualPayment = true;
        resolvedToken = undefined;
        resolvedTitle = '';
      }

      if (isTerminalCheckout && terminalResult?.mode === 'checkout') {
        const data: TerminalCheckoutResult = terminalResult.data;
        resolvedToken = data.token ?? resolvedToken;
        resolvedTransactionId = data.transaction_id ?? undefined;
        resolvedIntentId = data.payment_intent_id ?? undefined;
        if (data.card_brand) {
          resolvedTitle = `Terminal ${data.card_brand}`
            + (data.masked_pan ? ' ****' + data.masked_pan : '');
        }
      }

      // Saved-card / newly-tokenized gateway sale: no client-side capture. The
      // backend persists N package skeletons (declined=1) in Phase 1, then we
      // charge the card in Phase 2. Terminal (already captured) and manual/
      // cash/zero paths skip Phase 2.
      const willRunGatewayCharge =
        netChargeAmount > 0
        && !resolvedManualPayment
        && !isTerminalCheckout
        && paymentToken !== 'new'
        && !isTerminalMode
        && !!resolvedToken;

      const packages = packageLines.map((line): CreatePatientPackagesBatchItem => ({
        packageId: line.packageId,
        // Gross price — the server applies the discounts and stores the net.
        price: line.price,
        discount1Id: line.discount1Id ?? undefined,
        manualDiscount1: line.manualDiscount1 || undefined,
        discount2Id: line.discount2Id ?? undefined,
        manualDiscount2: line.manualDiscount2 || undefined,
      }));

      const result = await enrollInPackagesBatch(patientId, {
        packages,
        planId: planId ? Number(planId) : undefined,
        quoteId: quoteId ? Number(quoteId) : undefined,
        facilityId: facilityId ? Number(facilityId) : undefined,
        notes: notes.trim() || undefined,
        merchantId: merchantId ?? undefined,
        paymentMethodID: manualMethodId ?? selectedMethod?.paymentMethodID ?? undefined,
        paymentMethodToken: resolvedToken,
        paymentMethodTitle: resolvedTitle,
        chargeAmount: netChargeAmount,
        chargeTransactionId: resolvedTransactionId || undefined,
        chargeIntentId: resolvedIntentId || undefined,
        manualPayment: resolvedManualPayment || undefined,
        referenceNumber: referenceNumber.trim() || undefined,
        // The selector auto-attaches the patient's own cards, and keeps one
        // at $0 ("not needed") when the total doesn't need it. The API refuses
        // any card under $0.01, so a $0 card would fail the whole sale. Send
        // only cards that pay something, like the payment modals do.
        giftCards: giftCards
          .map((card) => ({
            giftCardId: card.giftCard.giftCardId ?? card.giftCard.paymentsCreditID,
            amount: card.amount,
          }))
          .filter(
            (card): card is { giftCardId: number; amount: number } =>
              typeof card.giftCardId === 'number' && card.giftCardId > 0 && card.amount > 0,
          ),
      });

      // Fire-and-forget mark-complete on the terminal_transaction_log row so
      // the patient-detail reconciler skips it. Mirrors ChargePaymentModal.
      if (isTerminalCheckout && terminalResult?.mode === 'checkout') {
        const logId = terminalResult.data.transaction_log_id;
        const newPaymentId = result.paymentId ?? undefined;
        if (typeof logId === 'number' && logId > 0) {
          void markTerminalTransactionComplete(logId, newPaymentId ?? undefined).catch((err) => {
            console.warn('[AddPackageModal] mark-complete failed', err);
          });
        }
      }

      // Phase 2: charge the saved card. The server captures the intent, writes
      // the real pmt_ PTA + intent bridge, and materializes ALL packages on the
      // payment on capture (ForwardChargeService loops every package row), or
      // voids the payment + leaves them declined on failure.
      if (willRunGatewayCharge && merchantId && resolvedToken && result.paymentId) {
        const charge = await chargeForwardPayment(result.paymentId, {
          merchant_id: merchantId,
          payment_method_id: resolvedToken,
          amount: netChargeAmount,
          patient_id: patientId,
        });
        if (!charge.success) {
          throw new DeclinedError(charge.message || 'Transaction error: Payment was declined.');
        }
      }

      return { status: 'success' as const };
    },
    onSuccess: () => {
      setResultStatus('success');
      setSaveOverlay('saved');
      onSaved();
      setTimeout(() => {
        setSaveOverlay(null);
        setStep('result');
      }, 800);
    },
    onError: (err: unknown) => {
      setSaveOverlay(null);
      if (err instanceof DeclinedError) {
        // The card was declined (and the packages left ungranted server-side).
        setResultStatus('declined');
        setDeclineMessage(err.message);
        setStep('result');
        return;
      }
      setError(getErrorMessage(err, 'Failed to process package purchase. Please try again.'));
      setStep('form');
    },
  });

  // Auto-submit once the terminal checkout completes — funds are captured at
  // Forward as soon as `terminalResult` lands. Without this, staff sees
  // "Approved" but the modal waits for a manual Confirm click; closing then
  // orphans the captured charge. Mirrors ChargePaymentModal (Fix 13).
  useEffect(() => {
    if (
      terminalResult?.mode === 'checkout'
      && step === 'form'
      && autoSubmittedRef.current !== terminalResult
      && !purchaseMutation.isPending
    ) {
      autoSubmittedRef.current = terminalResult;
      purchaseMutation.mutate(undefined);
    }
  }, [terminalResult, step, purchaseMutation]);

  if (!open) return null;

  const isPending = purchaseMutation.isPending;

  function handleConfirmPurchase() {
    setError(null);
    if (packageLines.length === 0) {
      setError('Add at least one package.');
      return;
    }

    // No payment method or merchant needed when there's nothing to charge.
    if (netChargeAmount === 0) {
      purchaseMutation.mutate(undefined);
      return;
    }

    // Manual payment (Cash, Check, etc.) — skip gateway entirely.
    if (isManualMode) {
      if (!facilityId) {
        setError('Select a facility.');
        return;
      }
      purchaseMutation.mutate(undefined);
      return;
    }

    if (!merchantId) {
      setError('Select a merchant account.');
      return;
    }
    if (isTerminalMode && !isTerminalCheckout) {
      setError('Run the terminal charge before confirming the purchase.');
      return;
    }
    if (!isTerminalMode && paymentToken === 'new') {
      setError('Complete new-card entry or select a saved payment source.');
      return;
    }
    // Terminal checkout, saved card, or tokenized gateway sale.
    purchaseMutation.mutate(undefined);
  }

  if (step === 'result') {
    const packageNames = packageLines.map((line) => line.name).filter(Boolean);
    return (
      <Modal isOpen={open} onClose={onClose} title="Add Package" size="xl" footer={<Button onClick={onClose}>Close</Button>}>
        <div className="space-y-3">
          <div className={`rounded-md px-4 py-3 text-sm ${
            resultStatus === 'success'
              ? 'border border-green-200 bg-green-50 text-green-800'
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}>
            {resultStatus === 'success' ? (
              <>
                <p className="font-semibold">
                  {packageNames.length > 1 ? 'Packages purchased successfully.' : 'Package purchased successfully.'}
                </p>
                {packageNames.length > 0 ? (
                  <p className="mt-1 text-xs">
                    {packageNames.length > 1 ? 'Packages: ' : 'Package: '}{packageNames.join(', ')}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <p className="font-semibold">Package purchase did not complete.</p>
                {declineMessage ? <p className="mt-1 text-xs">{declineMessage}</p> : null}
              </>
            )}
          </div>
          {saveWarning ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">Payment method not saved.</p>
              <p className="mt-1 text-xs">{saveWarning}</p>
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={open}
      onClose={isPending ? () => {} : onClose}
      title="Add Package"
      size="xl"
      footer={
        <div className="flex w-full items-center justify-end gap-4">
          {giftCardTotal > 0 ? (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-400 line-through">{fmtCurrency(packagesTotal)}</span>
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <Gift className="h-3.5 w-3.5" />
                -{fmtCurrency(giftCardTotal)}
              </span>
              <span className="text-lg font-bold text-slate-900">{fmtCurrency(netChargeAmount)}</span>
            </div>
          ) : (
            <span className="text-lg font-bold text-slate-900">{fmtCurrency(packagesTotal)}</span>
          )}
          <Button onClick={handleConfirmPurchase} disabled={!canSubmit || isPending}>
            Confirm Purchase
          </Button>
        </div>
      }
      overlay={
        saveOverlay ? (
          <div className="flex flex-col items-center gap-3">
            {saveOverlay === 'saving' ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-slate-600">Processing…</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <span className="text-sm font-medium text-green-700">Success</span>
              </>
            )}
          </div>
        ) : undefined
      }
    >
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-5">
        {/* ── SECTION 1: Packages ── */}
        <PackagesSection onChange={setPackageLines} />

        {/* ── SECTION 2: Payment & Billing ── */}
        <div className="rounded-lg border border-slate-200">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-slate-700">Payment & Billing</h3>
          </div>
          <div className="space-y-4 p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {!isManualMode && (
                <MerchantSelector value={merchantId} onChange={handleMerchantChange} />
              )}
              {isManualMode && (
                <label className="space-y-1 text-sm text-slate-700">
                  <span className="font-medium">Facility <span className="text-red-500">*</span></span>
                  <select
                    value={facilityId}
                    onChange={(e) => setFacilityId(e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select facility…</option>
                    {facilities.filter((f) => f.active).map((f) => (
                      <option key={f.facilityId} value={f.facilityId}>{f.facilityName}</option>
                    ))}
                  </select>
                </label>
              )}
              <PaymentSourceSelector
                patientId={patientId}
                value={paymentToken}
                merchantId={merchantId}
                onChange={handleSourceChange}
                showManualMethods
              />
            </div>
            {!isManualMode && (
              <ForwardBillingMethodCapture
                active={paymentToken === 'new'}
                patientId={patientId}
                merchantId={merchantId}
                onSaved={handleNewSourceSaved}
              />
            )}
            {!isManualMode && isTerminalMode && selectedTerminal ? (
              <TerminalCheckoutPanel
                terminal={selectedTerminal}
                patientId={patientId}
                amount={netChargeAmount}
                merchantId={merchantId}
                mode="checkout"
                onSuccess={handleTerminalResult}
                onReset={handleTerminalReset}
              />
            ) : null}
            {isManualMode && (
              <label className="space-y-1 text-sm text-slate-700">
                <span className="font-medium">Reference Number</span>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="Check #, receipt #, etc."
                  className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
            )}
            {saveWarning ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-semibold">Payment method not saved.</p>
                <p className="mt-1 text-xs">{saveWarning}</p>
              </div>
            ) : null}

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Notes</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>

            <div className="border-t border-slate-200 pt-4">
              <GiftCardSelector
                appliedCards={giftCards}
                onChange={setGiftCards}
                maxAmount={Math.max(0, packagesTotal)}
                patientId={patientId}
              />
            </div>

            {packagesTotal > 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span>Packages Total:</span><span>{fmtCurrency(packagesTotal)}</span></div>
                {giftCardTotal > 0 ? <div className="flex justify-between text-green-700"><span>Gift Cards:</span><span>-{fmtCurrency(giftCardTotal)}</span></div> : null}
                <div className="flex justify-between font-semibold border-t border-slate-300 pt-1"><span>Net Charge:</span><span>{fmtCurrency(netChargeAmount)}</span></div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
